import { FieldProps, useField } from 'informed';
import { Moment } from 'moment';
import Picker from 'rc-picker';
import { PickerProps, PickerTimeProps } from 'rc-picker/es/Picker';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import csCZ from 'rc-picker/lib/locale/cs_CZ';

import TyInputError from './TyInputError';
import { ClockIcon, CloseIcon } from '../icons';
import './TyInput.css';

type Props = Omit<Partial<PickerTimeProps<Moment>>, 'picker'> & {
  label?: string;
};

type TyTimeInputProps<Fields extends object> = FieldProps<Props, Moment, Fields>;

/**
 * @deprecated
 * Use TyDateInput instead
 */

const TyTimeInput = <Fields extends object>(props: TyTimeInputProps<Fields>) => {
  const {
    render,
    fieldState,
    userProps,
    fieldApi,
  } = useField<Props, Moment, Fields>(props);
  const { error, showError, focused, value } = fieldState;
  const { setValue, setTouched, setFocused } = fieldApi;
  const {
    id,
    label,
    className = '',
    format = ['HH:mm', 'H:mm'],
    generateConfig = momentGenerateConfig,
    locale = csCZ,
    open = focused,
    onChange,
    onFocus,
    onBlur,
    suffixIcon = <ClockIcon />,
    clearIcon = <CloseIcon />,
    showSecond = false,
    disabled = false,
    ...rest
  } = userProps;

  const innerOnChange: PickerProps<Moment>['onChange'] = (date, dateString) => {
    setValue(date ?? undefined);
    onChange?.(date, dateString);
  };
  const innerOnFocus: PickerProps<Moment>['onFocus'] = (e) => {
    setFocused(true, e);
    onFocus?.(e);
  };
  const innerOnBlur: PickerProps<Moment>['onBlur'] = (e) => {
    setTouched(true, e);
    setFocused(false, e);
    onBlur?.(e);
  };

  const focusedClassName = focused ? 'ty_input_field__focused' : '';
  const filledClassName = value ? 'ty_input_field__filled' : '';
  const disabledClassName = disabled === true ? 'ty_input_field--is-disabled' : '';

  return render(
    <div className={`ty_input_field ty_rc_picker ${showError && error ? 'ty_input_error' : ''} ${focusedClassName} ${filledClassName} ${disabledClassName} ${className}`}>
      <Picker<Moment>
        {...rest}
        className="ty_input"
        dropdownClassName="ty_input_dropdown"
        id={id}
        format={format}
        generateConfig={generateConfig}
        picker="time"
        locale={locale}
        value={value}
        showSecond={showSecond}
        open={open}
        onOpenChange={(o) => setFocused(o)}
        onChange={innerOnChange}
        onPanelChange={innerOnChange}
        onSelect={(time) => innerOnChange(time, '')}
        onFocus={innerOnFocus}
        onBlur={innerOnBlur}
        suffixIcon={suffixIcon}
        clearIcon={clearIcon}
        disabled={disabled}
      />
      {label ? <label htmlFor={id}>{label}</label> : null}
      <TyInputError
        fieldState={fieldState}
        required={props.required}
        validate={props.validate}
      />
    </div>,
  );
};

export default TyTimeInput;
